// src/pages/business/chats/BusinessChatListPage.tsx
// Mimics the mobile BusinessChatListScreen with enhanced web functionality

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Search,
  X,
  MessageSquare,
  Loader2,
} from 'lucide-react';
import { useChatScreen, ChatRoom } from '@/stores/useChatScreen';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import CreateGroupModal from '@/components/modals/CreateGroupModal';

const formatTimestamp = (timestamp: string | null) => {
  if (!timestamp) return '';
  const date = parseISO(timestamp);
  if (isToday(date)) return format(date, 'h:mm a');
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'dd/MM/yyyy');
};

// ── Chat Item Component ────────────────────────────────────────────────────────
const ChatItem: React.FC<{
  item: ChatRoom;
  unreadCount: number;
  onPress: () => void;
}> = ({ item, unreadCount, onPress }) => {
  // Get the other participant's info (customer for business owner)
  const getParticipantInfo = () => {
    if (item.type === 'business_specific' && item.business_id && item.participants && item.participants.length > 0) {
      const participant = item.participants[0];
      return {
        name: participant.full_name || 'Customer',
        avatar: participant.avatar_url,
      };
    }
    return {
      name: item.room_name || 'Chat',
      avatar: item.room_avatar_url,
    };
  };

  const { name, avatar } = getParticipantInfo();
  const initial = name.charAt(0).toUpperCase();

  return (
    <button
      onClick={onPress}
      className="w-full flex items-center gap-3 p-4 hover:bg-light-bg dark:hover:bg-dark-bg transition-colors text-left"
    >
      {/* Avatar Section */}
      <div className="relative flex-shrink-0">
        <div className="w-14 h-14 rounded-full overflow-hidden flex items-center justify-center bg-primary/10">
          {avatar ? (
            <img src={avatar} alt={name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-primary font-bold text-xl">{initial}</span>
          )}
        </div>
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 flex items-center justify-center border-2 border-light-card dark:border-dark-card">
            <span className="text-white text-xs font-bold">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          </div>
        )}
        
        {/* New Connection Dot */}
        {item.is_new_connection && (
          <div className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-light-card dark:border-dark-card" />
        )}
      </div>

      {/* Content Section */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <p className={`text-light-text dark:text-dark-text truncate ${unreadCount > 0 ? 'font-bold' : 'font-medium'}`} style={{ fontSize: 15 }}>
              {name}
            </p>
            {/* Group Badge */}
            {(item.is_group || item.group_name) && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300">
                GROUP
              </span>
            )}
          </div>
          <span className="text-xs text-light-subtle dark:text-dark-subtle flex-shrink-0">
            {formatTimestamp(item.last_message_created_at || null)}
          </span>
        </div>
        
        <p className={`text-sm truncate ${unreadCount > 0 ? 'font-semibold text-light-text dark:text-dark-text' : 'text-light-subtle dark:text-dark-subtle'}`}>
          {item.last_message_content || 'No messages yet'}
        </p>
      </div>
    </button>
  );
};

// ── Main Page Component ────────────────────────────────────────────────────────
const BusinessChatListPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Get chat data from store
  const chatRooms = useChatScreen((state) => state.chatRooms);
  const roomsLoading = useChatScreen((state) => state.roomsLoading);

  // Filter chat rooms for this specific business
  const businessChatRooms = useMemo(() => {
    if (!chatRooms) return [];
    return chatRooms.filter((room: ChatRoom) => {
      return room.business_id === businessId;
    });
  }, [chatRooms, businessId]);

  // Apply search filter
  const filteredRooms = useMemo(() => {
    if (!searchQuery.trim()) return businessChatRooms;
    const query = searchQuery.toLowerCase();
    return businessChatRooms.filter((room: ChatRoom) => {
      return room.room_name?.toLowerCase().includes(query);
    });
  }, [businessChatRooms, searchQuery]);

  // Calculate total unread for this business
  const totalUnread = useMemo(() => {
    return businessChatRooms.reduce(
      (sum: number, room: ChatRoom) => sum + (room.unread_count || 0),
      0
    );
  }, [businessChatRooms]);

  // Fetch rooms on mount and when business changes
  useEffect(() => {
    if (businessId) {
      useChatScreen.getState().fetchChatRooms(businessId);
    }
  }, [businessId]);

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border">
        <div className="px-4 py-3">
          {/* Top Row */}
          <div className="flex items-center gap-3 mb-3">
            <button
              onClick={() => navigate(`/business/${businessId}`)}
              className="flex-shrink-0 p-1"
            >
              <ArrowLeft size={24} className="text-light-text dark:text-dark-text" />
            </button>
            
            <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
              Business Chats
            </h1>

            {totalUnread > 0 && (
              <div className="px-2 py-0.5 rounded-full bg-red-500">
                <span className="text-white text-xs font-bold">
                  {totalUnread > 99 ? '99+' : totalUnread}
                </span>
              </div>
            )}

            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="ml-auto flex items-center gap-2 px-3 py-1.5 bg-primary/10 hover:bg-primary/20 dark:bg-golden/10 dark:hover:bg-golden/20 text-primary dark:text-golden text-sm font-semibold rounded-lg transition-colors"
            >
              New Group
            </button>
          </div>

          {/* Search Bar */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border">
            <Search size={18} className="text-light-subtle dark:text-dark-subtle flex-shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search conversations..."
              className="flex-1 bg-transparent text-sm text-light-text dark:text-dark-text placeholder-light-subtle dark:placeholder-dark-subtle focus:outline-none"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')}>
                <X size={16} className="text-light-subtle dark:text-dark-subtle" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Chat List */}
      <div className="max-w-5xl mx-auto">
        {roomsLoading ? (
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <Loader2 size={40} className="animate-spin mx-auto text-primary" />
              <p className="mt-4 text-light-subtle dark:text-dark-subtle">Loading chats...</p>
            </div>
          </div>
        ) : filteredRooms.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 px-8 text-center gap-4">
            <MessageSquare size={64} className="text-light-subtle dark:text-dark-subtle" style={{ opacity: 0.3 }} />
            <p className="font-semibold text-light-text dark:text-dark-text" style={{ fontSize: 18 }}>
              No conversations yet
            </p>
            <p className="text-sm text-light-subtle dark:text-dark-subtle">
              Chats with customers will appear here
            </p>
          </div>
        ) : (
          <div className="divide-y divide-light-border dark:divide-dark-border">
            {filteredRooms.map((room) => (
              <ChatItem
                key={room.room_id}
                item={room}
                unreadCount={room.unread_count || 0}
                onPress={() =>
                  navigate(`/business/${businessId}/chat/${room.room_id}`, {
                    state: {
                      roomName: room.room_name,
                      otherUserId: room.participants?.[0]?.id,
                    },
                  })
                }
              />
            ))}
          </div>
        )}
      </div>

      <CreateGroupModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        businessId={businessId}
        context="business"
      />
    </div>
  );
};

export default BusinessChatListPage;